import React, { useState } from 'react';
import {
  Box, List, ListItemButton, ListItemAvatar, ListItemText,
  Avatar, Typography, Badge, IconButton, InputBase, Tooltip,
  useTheme as useMuiTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  ChatBubbleOutline as EmptyIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { useChat } from '../../contexts/ChatContext';
import { useAppSelector } from '../../hooks/redux.hooks';
import { useLanguage } from '../../contexts/LanguageContext';
import { chatTranslations } from './chatTranslations';
// NewDirectDialog is rendered by ChatWidget (outside the overflow:hidden Paper)

// Deterministic avatar color based on name
const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
  'linear-gradient(135deg,#ffecd2,#fcb69f)',
  'linear-gradient(135deg,#ff9a9e,#fad0c4)',
];
const avatarGradient = (name: string) =>
  AVATAR_COLORS[(name.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

interface Props {
  onCreateGroup: () => void;
  onNewDirect: () => void;
}

const ConversationList: React.FC<Props> = ({ onCreateGroup, onNewDirect }) => {
  const { conversations, activeConversationId, setActiveConversation, closeChat } = useChat();
  const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
  const muiTheme = useMuiTheme();
  const { language } = useLanguage();
  const t = chatTranslations[language];
  const isRTL = language === 'ar';

  const [search, setSearch] = useState('');

  const filtered = conversations.filter(c =>
    !search || (c.name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isToday(d))     return format(d, 'HH:mm');
    if (isYesterday(d)) return t.yesterday;
    return format(d, 'dd/MM');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{
        px: 1.5, py: 1.2,
        background: muiTheme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 100%)'
          : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        display: 'flex', alignItems: 'center', gap: 0.5,
        flexShrink: 0,
      }}>
        <Typography variant="subtitle2" sx={{
          flex: 1, color: 'white', fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.3,
        }}>
          {t.messages}
        </Typography>

        <Tooltip title={t.newDirect}>
          <IconButton size="small" onClick={onNewDirect}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
            <PersonAddIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        {isSuperAdmin && (
          <Tooltip title={t.createGroup}>
            <IconButton size="small" onClick={onCreateGroup}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <AddIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t.close}>
          <IconButton size="small" onClick={closeChat}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Search ── */}
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          bgcolor: 'action.hover', borderRadius: 3, px: 1.5, py: 0.5,
          border: '1px solid', borderColor: 'divider',
          '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' },
          transition: 'all 0.2s',
        }}>
          <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <InputBase
            fullWidth
            placeholder={t.searchConversations}
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ fontSize: '0.8rem' }}
          />
        </Box>
      </Box>

      {/* ── Conversation List ── */}
      <List dense sx={{ flex: 1, overflowY: 'auto', p: 0, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 } }}>
        {filtered.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', py: 5, gap: 1, color: 'text.disabled',
          }}>
            <EmptyIcon sx={{ fontSize: 36, opacity: 0.4 }} />
            <Typography variant="caption">{t.noConversations}</Typography>
          </Box>
        ) : (
          filtered.map(conv => {
            const isActive = conv.conversationId === activeConversationId;
            const initials = (conv.name ?? '?').charAt(0).toUpperCase();
            const hasUnread = conv.unreadCount > 0;
            const otherOnline = !conv.isGroup && conv.members?.some(m => m.isOnline && m.fullName === conv.name);

            const lastMsgText = conv.lastMessage?.isDeleted
              ? t.messageDeleted
              : conv.lastMessage?.attachmentPath && !conv.lastMessage?.content
                ? `📎 ${t.attachment}`
                : conv.lastMessage?.content || '';

            return (
              <ListItemButton
                key={conv.conversationId}
                selected={isActive}
                onClick={() => setActiveConversation(conv.conversationId)}
                sx={{
                  py: 1, px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'flex-start',
                  position: 'relative',
                  transition: 'all 0.15s',
                  '&.Mui-selected': {
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
                    borderLeft: isRTL ? 'none' : '3px solid',
                    borderRight: isRTL ? '3px solid' : 'none',
                    borderColor: 'primary.main',
                    pl: isRTL ? 1.5 : 1.25,
                    pr: isRTL ? 1.25 : 1.5,
                    '&:hover': { bgcolor: alpha(muiTheme.palette.primary.main, 0.12) },
                  },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 46, mt: 0.25 }}>
                  <Badge
                    badgeContent={conv.unreadCount || 0}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem', minWidth: 16, height: 16, fontWeight: 700,
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Avatar sx={{
                        width: 36, height: 36, fontSize: 15, fontWeight: 700,
                        background: avatarGradient(conv.name ?? '?'),
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}>
                        {initials}
                      </Avatar>
                      {otherOnline && (
                        <Box sx={{
                          position: 'absolute', bottom: -1, [isRTL ? 'left' : 'right']: -1,
                          width: 12, height: 12, borderRadius: '50%',
                          bgcolor: '#00E676', border: '2px solid', borderColor: 'background.paper',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                        }} />
                      )}
                    </Box>
                  </Badge>
                </ListItemAvatar>

                <ListItemText
                  sx={{ my: 0, overflow: 'hidden' }}
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={hasUnread ? 700 : 500}
                        noWrap
                        sx={{ flex: 1, fontSize: '0.82rem', color: hasUnread ? 'text.primary' : 'text.secondary' }}
                      >
                        {conv.name ?? 'Unknown'}
                      </Typography>
                      {conv.lastMessage && (
                        <Typography variant="caption" sx={{
                          flexShrink: 0, fontSize: '0.62rem',
                          color: hasUnread ? 'primary.main' : 'text.disabled',
                          fontWeight: hasUnread ? 600 : 400,
                        }}>
                          {formatTime(conv.lastMessage.sentAt)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" noWrap sx={{
                      fontSize: '0.73rem',
                      color: hasUnread ? 'text.primary' : 'text.disabled',
                      fontWeight: hasUnread ? 500 : 400,
                    }}>
                      {lastMsgText || t.noMessages}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>

    </Box>
  );
};

export default ConversationList;
